// Client preview for slot-label line assignment CSV/JSON bulk import.

import { parseBulkImport } from "./bulkImportParser";
import { normalizeSlotLabel } from "./slotLabel";
import type { SlotCleanupMaterialLine } from "./slotLabelCleanupClient";

export type SlotLabelImportRow = {
  line_id: string | null;
  recipe_code: string;
  material_code: string;
  material_name: string;
  line_quantity: number | null;
  slot_label: string;
  resolved_line_id: string | null;
  current_slot_label: string | null;
  blocked: boolean;
  blockReason: string | null;
};

export type SlotLabelsBulkImportPreview =
  | { kind: "empty" }
  | { kind: "error"; error: string }
  | {
      kind: "ok";
      rows: SlotLabelImportRow[];
      totals: { applyable: number; blocked: number; unchanged: number };
    };

function cell(row: Record<string, unknown>, key: string): string {
  const v = row[key];
  if (v === null || v === undefined) return "";
  return String(v).trim();
}

function parseOptionalNumber(raw: string): number | null {
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function quantitiesMatch(a: number, b: number): boolean {
  return Math.abs(a - b) < 1e-9;
}

export function resolveSlotLabelImportLineId(
  input: {
    line_id: string | null;
    recipe_code: string;
    material_code: string;
    line_quantity: number | null;
  },
  catalog: ReadonlyArray<SlotCleanupMaterialLine>,
): { line_id: string | null; reason: string | null } {
  const lineId = input.line_id?.trim() || null;
  if (lineId) {
    const hit = catalog.find((ln) => ln.line_id === lineId);
    if (!hit) return { line_id: null, reason: `Unknown line_id: ${lineId}` };
    return { line_id: hit.line_id, reason: null };
  }

  const recipe = input.recipe_code.trim().toLowerCase();
  const material = input.material_code.trim().toLowerCase();
  if (!recipe || !material) {
    return { line_id: null, reason: "recipe_code and material_code are required without line_id." };
  }

  let matches = catalog.filter(
    (ln) =>
      ln.recipe_code.trim().toLowerCase() === recipe &&
      ln.material_code.trim().toLowerCase() === material,
  );
  if (input.line_quantity !== null) {
    matches = matches.filter((ln) => quantitiesMatch(ln.quantity, input.line_quantity!));
  }
  if (matches.length === 0) {
    return { line_id: null, reason: "No matching BOM line found." };
  }
  if (matches.length > 1) {
    return {
      line_id: null,
      reason: `Ambiguous match (${matches.length} lines) — provide line_id or line_quantity.`,
    };
  }
  return { line_id: matches[0]!.line_id, reason: null };
}

export function buildSlotLabelsBulkImportPreview(
  text: string,
  catalog: ReadonlyArray<SlotCleanupMaterialLine>,
): SlotLabelsBulkImportPreview {
  const trimmed = text.trim();
  if (!trimmed) return { kind: "empty" };

  const parsed = parseBulkImport(trimmed);
  if (!parsed.ok) return { kind: "error", error: parsed.error };

  const rows: SlotLabelImportRow[] = [];
  const seenLineIds = new Set<string>();

  for (const raw of parsed.rows) {
    const slot_label = normalizeSlotLabel(cell(raw, "slot_label"));
    const line_id_raw = cell(raw, "line_id") || null;
    const recipe_code = cell(raw, "recipe_code");
    const material_code = cell(raw, "material_code");
    const material_name = cell(raw, "material_name");
    const line_quantity = parseOptionalNumber(cell(raw, "line_quantity"));

    let blocked = false;
    let blockReason: string | null = null;

    if (!slot_label) {
      blocked = true;
      blockReason = "slot_label is required (non-empty after normalization).";
    }

    const resolved = resolveSlotLabelImportLineId(
      { line_id: line_id_raw, recipe_code, material_code, line_quantity },
      catalog,
    );
    if (!blocked && resolved.reason) {
      blocked = true;
      blockReason = resolved.reason;
    }
    if (!blocked && resolved.line_id && seenLineIds.has(resolved.line_id)) {
      blocked = true;
      blockReason = "Duplicate line_id in import.";
    }
    if (!blocked && resolved.line_id) seenLineIds.add(resolved.line_id);

    const current =
      resolved.line_id != null
        ? (catalog.find((ln) => ln.line_id === resolved.line_id)?.slot_label ?? null)
        : null;

    rows.push({
      line_id: line_id_raw,
      recipe_code,
      material_code,
      material_name,
      line_quantity,
      slot_label,
      resolved_line_id: resolved.line_id,
      current_slot_label: current,
      blocked,
      blockReason,
    });
  }

  const applyable = rows.filter(
    (r) => !r.blocked && r.resolved_line_id && r.current_slot_label !== r.slot_label,
  ).length;
  const unchanged = rows.filter(
    (r) => !r.blocked && r.resolved_line_id && r.current_slot_label === r.slot_label,
  ).length;

  return {
    kind: "ok",
    rows,
    totals: {
      applyable,
      blocked: rows.filter((r) => r.blocked).length,
      unchanged,
    },
  };
}
